'use client'

import React from 'react'
import { Check, Plus, User } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { Doc } from '@/convex/_generated/dataModel'

type PatientDoc = Doc<'patients'>

interface StepPatientSelectProps {
  matches: PatientDoc[]
  selectedPatientId: string | null
  onSelectPatient: (patient: PatientDoc) => void
  onAddNewPatient: () => void
  onPrevious: () => void
  onNext: () => void
}

const StepPatientSelect: React.FC<StepPatientSelectProps> = ({
  matches,
  selectedPatientId,
  onSelectPatient,
  onAddNewPatient,
  onPrevious,
  onNext,
}) => {
  const hasSelection = !!selectedPatientId

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-5">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
          Select the patient profile
        </h2>
        <p className="mt-2 text-sm text-gray-600 sm:text-base">
          We found more than one ReACH profile linked to this number.
        </p>
      </div>

      <Card className="border shadow-lg">
        <CardContent className="flex flex-col gap-3 px-4 py-5 sm:px-6 sm:py-6">
          <div className="overflow-hidden rounded-xl border bg-white">
            <ul className="divide-y">
              {matches.map((patient) => {
                const id = String(patient._id)
                const selected = selectedPatientId === id
                const patientName = (patient as any).name || 'Unnamed Patient'
                const patientHumanId =
                  (patient as any).patient_id || 'ReACH profile'
                const gender = (patient as any).gender

                return (
                  <li
                    key={id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    onClick={() => onSelectPatient(patient)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onSelectPatient(patient)
                      }
                    }}
                    className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-3 transition-colors ${
                      selected ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                          selected
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <User className="h-4 w-4" />
                      </div>

                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {patientName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {patientHumanId}
                          {gender && (
                            <>
                              {' · '}
                              {String(gender)}
                            </>
                          )}
                        </p>
                      </div>
                    </div>

                    {selected && (
                      <Check className="h-5 w-5 shrink-0 text-blue-600" />
                    )}
                  </li>
                )
              })}

              <li
                role="button"
                tabIndex={0}
                onClick={onAddNewPatient}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onAddNewPatient()
                  }
                }}
                className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-gray-50"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Add a new patient
                  </p>
                  <p className="text-xs text-gray-500">
                    Use this number for a new ReACH profile.
                  </p>
                </div>

                <Plus className="h-5 w-5 shrink-0 text-gray-700" />
              </li>
            </ul>
          </div>

          <div className="flex items-center justify-between pt-3">
            <Button type="button" variant="outline" onClick={onPrevious}>
              Back
            </Button>

            <Button
              type="button"
              onClick={onNext}
              disabled={!hasSelection}
              className="rounded-full bg-blue-600 px-6 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default StepPatientSelect
